var _ = require('underscore');
var TorsoService = require('backbone-torso/modules/ServiceCell');

var BARCODE_ERRORS = {
  'NO_DATA': 'noData',
  'TIMEOUT': 'timeout',
  'INVALID_BARCODE': 'invalid',
  'SCANNING_ERROR': 'scanningError'
};

/* jshint ignore:start */
var BARCODE_SCAN_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
  ERROR: 'ERROR',
  TIMEOUT: 'TIMEOUT'
};
var VECNA_ID_REGEX_STRING = '[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-4[A-Fa-f0-9]{3}-[89ABab][A-Fa-f0-9]{3}-[A-Fa-f0-9]{12}';
var BARCODE_INFO_REGEX = new RegExp(VECNA_ID_REGEX_STRING + '\\^' + VECNA_ID_REGEX_STRING);

/* Polling interval in ms */
var POLLING_INTERVAL = 250;
/**
 * Polling timeout in ms
 * Polling timeout cannot be equal to barcode scanning timeout as introduces a race condition
 * Polling timeout should be longer than barcode scanning timeout to ensure that the barcode scanning application exits correctly
 */
var POLLING_TIMEOUT = 31000;
/**
 * Barcode scanning timeout in ms
 * Barcode scanning timeout cannot be equal to polling timeout as introduces a race condition
 *
 * TO-DO : Make Android and IOS Ereg Launchers respect this value.
 * Timeout is currently handled by a hardcoded value in both rather than the passed BARCODE_SCANNING_TIMEOUT in the Ereg Launchers
 * (There is some misleading code that might make it appear to be used when passed through startBarcodeScan)
 */
var BARCODE_SCANNING_TIMEOUT = 30000;

function doesBarcodeJavascriptInterfaceExists() {
  try {
    return !_.isUndefined(BarcodeJavascriptInterface);
  } catch (interfaceExistsError) {
    if (interfaceExistsError instanceof ReferenceError) {
      return false;
    }
    throw interfaceExistsError;
  }
}
/* jshint ignore:end */

/**
 * Singleton service for managing the barcode integration
 *
 * @class barcodeScanningService
 * @static
 * @extends Torso.ServiceCell
 */
var barcodeScanningService = new (TorsoService.extend({

  /**
   * @method initialize
   * @override
   */
  initialize: function() {
    _.bindAll(this, 'startBarcodeScanAndPolling', '_setMockBarcodeInfo', '_setMockBarcodeCanceled', '_setMockBarcodeErrored');
    window.setMockBarcodeInfo = this._setMockBarcodeInfo;
    window.setMockBarcodeCanceled = this._setMockBarcodeCanceled;
    window.setMockBarcodeErrored = this._setMockBarcodeErrored;
  },

  /**
   * Clean up global methods.
   * @private
   */
  _dispose: function() {
    delete window.setMockBarcodeInfo;
    delete window.setMockBarcodeCanceled;
    delete window.setMockBarcodeErrored;
  },

  /**
   * @method isBarcodeScanningFinished
   * @returns {Boolean} whether or not barcode scanning is finished
   */
  isBarcodeScanningFinished: function() {
    return this.get('barcodeScanningFinished');
  },

  /**
   * @method getError
   * @returns {String} the error set on the viewState
   */
  getError: function() {
    return this.get('error');
  },

  /**
   * @method isCanceled
   * @returns {Boolean} whether or not the barcode scan was canceled
   */
  isCanceled: function() {
    return this.get('canceled');
  },

  /**
   * @method getPatientVecnaId
   * @returns {String} the patient Vecna ID from the barcode
   */
  getPatientVecnaId: function() {
    return this.get('patientVecnaId');
  },

  /**
   * @method getAppointmentVecnaId
   * @returns {String} the appointment Vecna ID from the barcode
   */
  getAppointmentVecnaId: function() {
    return this.get('appointmentVecnaId');
  },

  /**
   * Resets scanning related attributes to what their starting values should be
   * @method resetForNewScan
   */
  resetForNewScan: function() {
    this.unset('patientVecnaId');
    this.unset('appointmentVecnaId');
    this.unset('error');
    this.set('canceled', false);
  },

  /* jshint ignore:start */
  /**
   * Starts the barcode scanning and polling
   * @method _startBarcodeScanAndPolling
   */
  startBarcodeScanAndPolling: function() {
    this.resetForNewScan();
    this.set('barcodeScanningFinished', false);
    if (doesBarcodeJavascriptInterfaceExists()) {
      BarcodeJavascriptInterface.resetBarcodeScan();
      BarcodeJavascriptInterface.startBarcodeScan(BARCODE_SCANNING_TIMEOUT);
    } else {
      this._startMockBarcodeScan(BARCODE_SCANNING_TIMEOUT);
    }
    this._startPolling();
  },

  /**
   * Starts polling the android app for barcode scan status
   * @method _startPolling
   * @private
   */
  _startPolling: function() {
    var self = this;
    self.set('canceled', false);
    this._clearTimeoutsAndIntervals();
    this._pollingId = window.setInterval(function() {
      var barcodeScanStatus = self._getBarcodeScanStatus();
      // if the barcode scan is in progress, keep polling
      if (barcodeScanStatus === BARCODE_SCAN_STATUS.IN_PROGRESS) {
        return;
      }

      if (barcodeScanStatus === BARCODE_SCAN_STATUS.FINISHED) {
        var barcodeInfo = self._getBarcodeInfo();
        if (!barcodeInfo) {
          self.set('error', BARCODE_ERRORS.NO_DATA);
        } else if (!BARCODE_INFO_REGEX.test(barcodeInfo)) {
          self.set('error', BARCODE_ERRORS.INVALID_BARCODE);
        } else {
          var splitBarcodeInfo = barcodeInfo.split('^');
          self.set('patientVecnaId', splitBarcodeInfo[0]);
          self.set('appointmentVecnaId', splitBarcodeInfo[1]);
        }
      } else if (barcodeScanStatus === BARCODE_SCAN_STATUS.CANCELED) {
        self.set('canceled', true);
      } else if (barcodeScanStatus === BARCODE_SCAN_STATUS.ERROR) {
        self.set('error', BARCODE_ERRORS.SCANNING_ERROR);
      } else if (barcodeScanStatus === BARCODE_SCAN_STATUS.TIMEOUT) {
        self.set('error', BARCODE_ERRORS.TIMEOUT);
      }
      self._stopPolling();
    }, POLLING_INTERVAL);

    // only poll for the barcode scan status for POLLING_TIMEOUT ms
    this._timeoutId = window.setTimeout(function() {
      self.set('error', BARCODE_ERRORS.NO_DATA);
      self._stopPolling();
    }, POLLING_TIMEOUT);
  },

  /**
   * @method _getBarcodeScanStatus
   * @return {String} the status of the current barcode scan.  Will return a mock value if the BarcodeJavascriptInterface isn't available.
   * @private
   */
  _getBarcodeScanStatus: function() {
    var barcodeScanStatus;
    if (doesBarcodeJavascriptInterfaceExists()) {
      barcodeScanStatus = BarcodeJavascriptInterface.getBarcodeScanStatus();
    } else {
      barcodeScanStatus = this._getMockScanStatus();
    }
    return barcodeScanStatus;
  },

  /**
   * Get the barcode info either from the device or from mock.
   * @method _getBarcodeInfo
   * @return {String} the barcode info to set in the form: "<patientVecnaId>^<appointmentVecnaId>"
   * @private
   */
  _getBarcodeInfo: function() {
    var barcodeInfo;
    if (doesBarcodeJavascriptInterfaceExists()) {
      barcodeInfo = BarcodeJavascriptInterface.getBarcodeInfo();
    } else {
      barcodeInfo = this._getMockBarcodeInfo();
    }
    return barcodeInfo;
  },

  /**
   * Start a mock scan process if BarcodeJavascriptInterface isn't available.
   * @method _startMockBarcodeScan
   * @param barcodeScanningTimeout {Number} the number of ms to wait for a value before timing out the mock process.
   * @private
   */
  _startMockBarcodeScan: function(barcodeScanningTimeout) {
    this.unset('mockBarcodeInfo');
    this.set('mockBarcodeScanStatus', BARCODE_SCAN_STATUS.IN_PROGRESS);
    var self = this;
    this._mockScanTimeoutId = window.setTimeout(function() {
      self.set('mockBarcodeScanStatus', BARCODE_SCAN_STATUS.TIMEOUT);
    }, barcodeScanningTimeout);
  },

  /**
   * Globally exposed function to set barcode info for testing.
   * @method _setMockBarcodeInfo
   * @param barcodeInfo {String} the barcode info to set in the form: "<patientVecnaId>^<appointmentVecnaId>"
   * @private
   */
  _setMockBarcodeInfo: function(barcodeInfo) {
    this.set('mockBarcodeInfo', barcodeInfo);
    this.set('mockBarcodeScanStatus', BARCODE_SCAN_STATUS.FINISHED);
    this._clearMockBarcodeTimeouts();
  },

  /**
   * Globally exposed function to set a canceled status for testing.
   * @method _setMockBarcodeCanceled
   * @private
   */
  _setMockBarcodeCanceled: function() {
    this.set('mockBarcodeScanStatus', BARCODE_SCAN_STATUS.CANCELED);
  },

  /**
   * Globally exposed function to set an errored status for testing.
   * @method _setMockBarcodeErrored
   * @private
   */
  _setMockBarcodeErrored: function() {
    this.set('mockBarcodeScanStatus', BARCODE_SCAN_STATUS.ERROR);
  },
  /* jshint ignore:end */

  /**
   * Stops polling for the barcode scan status
   * @method _stopPolling
   * @private
   */
  _stopPolling: function() {
    this.set('barcodeScanningFinished', true);
    this._clearTimeoutsAndIntervals();
  },

  /**
   * Clears the timeouts and intervals setup by this service.
   * @method _clearTimeoutsAndIntervals
   * @private
   */
  _clearTimeoutsAndIntervals: function() {
    window.clearInterval(this._pollingId);
    window.clearTimeout(this._timeoutId);
    this._clearMockBarcodeTimeouts();
  },

  /**
   * Clears the timeouts associated with the mock barcode process.
   * @method _clearMockBarcodeTimeouts
   * @private
   */
  _clearMockBarcodeTimeouts: function() {
    window.clearTimeout(this._mockScanTimeoutId);
  },

  /**
   * Get the mock barcode info if it is set.
   * @method _getMockBarcodeInfo
   * @return {String} the barcode info to set in the form: "<patientVecnaId>^<appointmentVecnaId>"
   * @private
   */
  _getMockBarcodeInfo: function() {
    return this.get('mockBarcodeInfo');
  },

  /**
   * Get the mock scan status.
   * @method _getMockScanStatus
   * @return {BARCODE_SCAN_STATUS} the status of the mock scan.
   * @private
   */
  _getMockScanStatus: function() {
    return this.get('mockBarcodeScanStatus');
  }
}))();

barcodeScanningService.BARCODE_ERRORS = BARCODE_ERRORS;
module.exports = barcodeScanningService;
